import { Component, ElementRef, inject, OnInit, Renderer2, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UserLoginService } from '../Common/UserLoginService/userlogin.service';
import { AuthenticationRequest, UserLoginAuthResponseDTO, UserLoginDTO } from '../Common/UserLoginService/userlogin.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../Shared/loader/loader.service';
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CompanyMasterDTO } from '../AdvERP/companies/companies.interface';
import { CompaniesService } from '../AdvERP/companies/companies.service';
import { BranchMasterService } from '../Common/BranchMasterService/branchmaster.service';
import { BranchMasterDTO, BranchMasterReadByUserIdRequestDTO } from '../Common/BranchMasterService/branchmaster.interface';
import { WhiteLabelServiceService } from '../AdvERP/companies/white-label/white-label-service.service';
import { CompanyWhiteLabelDTO, CompanyWhiteLabelReadByTenantIdRequestDTO } from '../AdvERP/companies/white-label/white-label.interface';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { style } from '@angular/animations';
import { SubRolesService } from '../Common/SubRoleService/sub-roles.service';
import { SubRolesReadByUserIdRequestDTO } from '../Common/SubRoleService/SubRoles.interface';
import { FilteredKeyValueRequestDTO } from '../Common/ValueListService/valuelist.interface';
import { ValueListService } from '../Common/ValueListService/value-list.service';
import { ApiConfigurationService } from '../Common/ApiConfigurationService/api-configuration.service';
import { PushNotificationService } from '../Common/PushNotificationService/pushnotification.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit {
  @ViewChild('loginErrorRef') loginErrorRef!: ElementRef<HTMLImageElement>;
  @ViewChild('companyLargeLogoRef') companyLargeLogoRef!: ElementRef<HTMLImageElement>;
  @ViewChild('companySmallLogoRef') companySmallLogoRef!: ElementRef<HTMLImageElement>;
  @ViewChild('companyCover', { static: true }) companyCoverRef!: ElementRef<HTMLElement>;



  loginError: string | null = null;
  // The form is not rendered until the tenant config + white-label are in (or
  // the host maps to no tenant and the company picker must be shown); the
  // loader spins until then.
  formVisible = false;
  showBranchSelector: boolean = false
  companyService = inject(CompaniesService);
  subRolesService = inject(SubRolesService)
  sanitizer = inject(DomSanitizer)
  branchMasterService = inject(BranchMasterService)
  BranchList: BranchMasterDTO[] = []
  isLoading: boolean = false;
  companyList: CompanyMasterDTO[] = []
  companyLoading: boolean = false;
  selectedCompanyId = 0;
  selectedTenantBranchId = 0;
  private route = inject(ActivatedRoute)
  valuelistService = inject(ValueListService)
  apiConfigurationService = inject(ApiConfigurationService)
  pushNotificationService = inject(PushNotificationService)
  renderer = inject(Renderer2)
  el = inject(ElementRef)
  destroy$ = new Subject<any>;
  constructor(
    private userLoginService: UserLoginService,
    private router: Router,
    private loaderService: LoaderService
  ) {
    // Username/password are enabled ONLY by initializeTenantContext() once the
    // tenant's ApiConfiguration + white-label have loaded - never merely
    // because a companyId value exists - so the form can't be submitted
    // against the wrong gateway and surface a misleading "login failed".
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe((searchTerm) => {
      this.companyLoading = true
      this.companyService.CompanyMaster_SearchByNameLogin({ cName: searchTerm }).subscribe(res => {
        this.companyList = res.items
        this.companyLoading = false
      })

    });
  }

  ngOnInit(): void {
    console.log('oninit initialized');

    if (this.userLoginService.isAuthenticated() && localStorage.getItem('loggedInBranchId')) {
      this.router.navigate(['/']);
    } else {
      const queryParamMap = this.route.snapshot.queryParamMap;
      // Query param NAMES are case-sensitive, and this URL is shared/typed
      // manually (nothing in-app generates it), so accept whatever casing
      // was actually used instead of requiring the exact 'companyid'/
      // 'branchid' spelling - a mismatch here silently fell through to the
      // localhost-default tenant (1) instead of the one in the URL.
      const getParamCaseInsensitive = (name: string): string | null => {
        const matchedKey = queryParamMap.keys.find(k => k.toLowerCase() === name.toLowerCase());
        return matchedKey ? queryParamMap.get(matchedKey) : null;
      };
      const companyIdParam = getParamCaseInsensitive('companyid');
      const branchIdParam = getParamCaseInsensitive('branchid');

      if (companyIdParam) {

        this.selectedCompanyId = +companyIdParam;
        this.selectedTenantBranchId = branchIdParam ? +branchIdParam : 0;

        if (this.selectedCompanyId && this.selectedCompanyId > 0) {
          localStorage.setItem('DefaultCompany', this.selectedCompanyId.toString());
          localStorage.setItem('CompanyId', this.selectedCompanyId.toString());
          localStorage.setItem('BranchId', this.selectedTenantBranchId.toString());
          this.LoginForm.controls.companyId.patchValue(this.selectedCompanyId);
          this.initializeTenantContext(this.selectedCompanyId, this.selectedTenantBranchId);
        }

      } else {

        const host = window.location.host;
        if (host.includes('localhost')) {
          this.selectedCompanyId = 1;
          this.selectedTenantBranchId = 1;
          localStorage.setItem('CompanyId', '1');
          localStorage.setItem('BranchId', '1');
          this.LoginForm.controls.companyId.patchValue(1);
          // No AM/VL host lookup on localhost - there's no response to pull a
          // tenant id from, so fall back to the same hardcoded id used for
          // white-labeling on this branch.
          this.initializeTenantContext(1, 1);
        } else {

          const data: FilteredKeyValueRequestDTO = {
            dCode: 'HostTenantId_Extended',
            tenantId: 1,
            tenantBranchId: 1,
            FilterValue: host
          };
          this.loaderService.show()

          this.valuelistService.ValueListItem_GetFilteredKeyValueExtendedByCode(data).subscribe({
            next: res => {
              if (res.items.length > 0) {
                this.selectedCompanyId = +res.items[0]?.key;
                this.selectedTenantBranchId = +(res.items[0]?.TenantBranchId ?? 0);
                localStorage.setItem('DefaultCompany', res.items[0]?.key);
                localStorage.setItem('CompanyId', this.selectedCompanyId.toString());
                localStorage.setItem('BranchId', this.selectedTenantBranchId.toString());
                this.LoginForm.controls.companyId.patchValue(this.selectedCompanyId);
                // Resolve this tenant's per-module gateway URLs before the login
                // form can be used (see initializeTenantContext).
                this.initializeTenantContext(this.selectedCompanyId, this.selectedTenantBranchId);
              } else {
                // Host isn't mapped to a tenant: leave the company picker
                // visible; initializeTenantContext runs once one is chosen.
                this.formVisible = true;
                this.loaderService.dismiss();
              }
            },
            error: err => {
              console.error('Error resolving tenant from host:', err);
              this.loaderService.dismiss();
              this.showSetupError();
            }
          });
        }
      }
    }
  }

  LoginForm = new FormGroup({
    userName: new FormControl({ value: '', disabled: true }, [Validators.required]),
    password: new FormControl({ value: '', disabled: true }, [Validators.required]),
    companyId: new FormControl(0),
  });

  loginUser(): void {
    Object.keys(this.LoginForm.controls).forEach(key => {
      const control = this.LoginForm.get(key);
      if (control?.invalid) {
        console.warn(`Invalid control: ${key}`, control.errors);
        control.markAsTouched(); // optional
      }
    });
    if (this.LoginForm.invalid) return;

    this.loaderService.show();
     let userdata: AuthenticationRequest = {
      userName: this.LoginForm.value.userName as string,
      password: this.LoginForm.value.password as string,
      tenantId: this.LoginForm.value.companyId?.toString() as string
    };

    this.userLoginService.UserLogin_auth(userdata).subscribe(
      (authRes: UserLoginAuthResponseDTO) => {


        if (authRes.data.designation && authRes.data.designation.length > 8) {
          this.loginErrorRef.nativeElement.style.display = 'block';
          this.loginErrorRef.nativeElement.innerHTML = authRes.data.designation;
        } else {
          this.loginErrorRef.nativeElement.style.display = 'none';
          this.loginErrorRef.nativeElement.innerHTML = ''; // optional: clear message
        }

        const token = authRes.data.token.trim();
        if (token !== "") {
          localStorage.setItem('access_token', token);
          // Persistent login: a tenant with RefreshTokenDays = 0 gets none, so never keep a stale one.
          if (authRes.data.refreshToken) localStorage.setItem('refresh_token', authRes.data.refreshToken);
          else localStorage.removeItem('refresh_token');


        }

        const request: BranchMasterReadByUserIdRequestDTO = {
          tenantId: Number(this.LoginForm.controls.companyId.value),
          actionUser: authRes.data.userId.toString(),
        };

        // Fetch branch information
        this.branchMasterService.BranchMaster_readByUserId(request).subscribe(
          (branchRes) => {


            if (branchRes.items.length === 1) {
              // Process login success if only 1 branch
              this.processLoginSuccess(branchRes.items[0]);
            } else {
              this.showBranchSelector = true
              this.BranchList = branchRes.items;
              this.loaderService.dismiss();
            }
          },
          (branchError) => {
            this.loaderService.dismiss();
            console.error('Error fetching branch data:', branchError);
          }
        );
      },
      (authError) => {
        this.loaderService.dismiss();
        // Not SignOutUser() here - the user was never logged in yet (no token
        // was ever set), so there's nothing to sign out of. SignOutUser()
        // also wipes the tenant's just-resolved ApiConfigurationService
        // override, which meant a transient failure on this call (500,
        // timeout, network) permanently downgraded every following retry to
        // the compiled-in default gateway URL instead of this tenant's real
        // one - masking the actual error behind a wrong-server symptom.
        this.loginErrorRef.nativeElement.style.display = 'block';
        this.loginErrorRef.nativeElement.innerHTML = 'Unable to log in right now. Please try again.';
        console.error('Authentication error:', authError);
      }
    );
  }

  processLoginSuccess(branch: BranchMasterDTO) {
    this.userLoginService.UserLogin_validateToken().subscribe(
      (auth) => {
        auth.loggedInBranchId = branch.tenantBranchId;
        localStorage.setItem('loggedInBranchId', branch.tenantBranchId.toString())
        localStorage.setItem('CompanyId', this.selectedCompanyId.toString());
        localStorage.setItem('BranchId', branch.tenantBranchId.toString());
        const body: SubRolesReadByUserIdRequestDTO = {
          userId: auth.userId,
          tenantId: this.selectedCompanyId,
          tenantBranchId: branch.tenantBranchId,
          softwareId: +environment.SoftwareId
        };
        this.subRolesService.SubRoles_readByUserId(body).subscribe(userMenu => {
          let menulist = userMenu.items.map(res => res.subRoleCode)
          localStorage.setItem('menuList', menulist.toString())
        })
        this.loaderService.dismiss();
        this.userLoginService.saveUserData(auth);
        // Fire-and-forget: registers this browser's FCM device token against
        // this user (see PushNotificationService for why failures here never
        // block login).
        this.pushNotificationService.registerCurrentDevice(auth.userId, this.selectedCompanyId, branch.tenantBranchId, auth.userId.toString());
        this.router.navigate(['/']);
      },
      (error) => {
        console.error('Error validating token:', error);
      }
    );
  }
  searchSubject = new Subject<string>();
  onNgSearch(event: { term: string; items: any[]; }) {
    this.searchSubject.next(event.term)
  }

  selectBranch(branch: BranchMasterDTO) {
    this.userLoginService.UserLogin_validateToken().subscribe(auth => {
      auth.loggedInBranchId = branch.tenantBranchId;
      localStorage.setItem('loggedInBranchId', branch.tenantBranchId.toString());
      localStorage.setItem('CompanyId', this.selectedCompanyId.toString());
      localStorage.setItem('BranchId', branch.tenantBranchId.toString());
      this.userLoginService.saveUserData(auth);
      // Same device-registration call as processLoginSuccess() — this branch
      // is the multi-branch manual-selection path, the other is single-branch
      // auto-login, both are genuine "user just finished logging in" points.
      this.pushNotificationService.registerCurrentDevice(auth.userId, this.selectedCompanyId, branch.tenantBranchId, auth.userId.toString());
      localStorage.setItem('selected_branch', JSON.stringify(branch));
      this.router.navigate(['/']);
    })
  }

  getLogoLiftPx(): number {
    return Math.min(10 + this.BranchList.length * 2, 34);
  }

  whileLabelService = inject(WhiteLabelServiceService)
  whiteLabelDetails!: CompanyWhiteLabelDTO

  // Loads this tenant's ApiConfiguration (gateway URL overrides) FIRST, then
  // only starts the white-label lookup once that's done - not in parallel.
  // ApiUrlRewriteInterceptor only rewrites a request's origin once
  // ApiConfigurationService.getOverrides() is non-null, which only happens
  // after loadConfiguration's response lands. Firing white-label (or worse,
  // enabling the login form so /auth could fire) before that completes
  // meant those requests fell back to the compiled-in default environment
  // URLs instead of this tenant's actual assigned server - exactly the
  // wrong-server routing bug this sequencing fixes. The loader stays up and
  // the username/password fields stay disabled for the whole chain, so
  // there's no way to submit login before the correct URLs are in place.
  private initializeTenantContext(tenantId: number, tenantBranchId: number): void {
    this.hideLoginMessage();
    this.LoginForm.controls.userName.disable();
    this.LoginForm.controls.password.disable();
    this.loaderService.show();

    this.apiConfigurationService.loadConfiguration(tenantId).pipe(
      // loadConfiguration swallows its own errors and emits null; continuing
      // would query white-label on the default gateway - treat it as failure.
      switchMap(config => {
        if (!config) throw new Error('ApiConfiguration could not be loaded');
        return this.whileLabelService.CompanyWhiteLabel_readByTenantId({ tenantId, tenantBranchId });
      })
    ).subscribe({
      next: (res) => {
        this.whiteLabelDetails = res as CompanyWhiteLabelDTO;
        try {
          this.updateFavicon();
        } catch (e) {
          console.error('Error applying white-label:', e);
        }
        this.loaderService.dismiss();
        this.formVisible = true;
        this.LoginForm.patchValue({ companyId: tenantId });
        this.LoginForm.controls.userName.enable();
        this.LoginForm.controls.password.enable();
      },
      error: (err) => {
        console.error('Error initializing tenant context (ApiConfiguration/white-label):', err);
        this.loaderService.dismiss();
        this.showSetupError();
      }
    });
  }

  // Fired by the company picker (only shown when the host maps to no tenant).
  onCompanyPicked(company: CompanyMasterDTO | undefined): void {
    if (!company?.tenantId) return;
    localStorage.setItem('CompanyId', company.tenantId.toString());
    this.initializeTenantContext(company.tenantId, 0);
  }

  private showSetupError(): void {
    const el = this.loginErrorRef?.nativeElement;
    if (!el) return;
    el.style.display = 'block';
    el.innerHTML = 'Unable to load the company configuration. This is not a login problem - please refresh the page, or contact support if it continues.';
  }

  private hideLoginMessage(): void {
    const el = this.loginErrorRef?.nativeElement;
    if (!el) return;
    el.style.display = 'none';
    el.innerHTML = '';
  }

  getBranchHTML(branches: any[]): string {
    return `<ul class="list-group">
      ${branches.map((branch, i) => `
        <li class="list-group-item branch-item ${branch.isDefault === 1 ? 'active' : ''}" data-index="${i}" style="cursor: pointer;" >
          <span>${branch.branchCode}</span>
          <span>${branch.branchName}</span>
        </li>`).join('')}
    </ul>`;
  }

  updateFavicon() {
    if (this.whiteLabelDetails) {
      const { favicon, primaryColor, secondaryColor, companyLogo, smallLogoUrl, loginPageHtml, styleCss } = this.whiteLabelDetails;
      const root = document.documentElement;
      this.renderer.setStyle(root, '--primary-color', primaryColor || '#0a63c6');
      this.renderer.setStyle(root, '--secondary-color', secondaryColor || '#12a07a');
      this.renderer.setStyle(root, '--primary-rgb', this.toRgbTriplet(primaryColor, '10, 99, 198'));
      this.renderer.setStyle(root, '--secondary-rgb', this.toRgbTriplet(secondaryColor, '18, 160, 122'));

      const style = this.renderer.createElement('style');
      const css = styleCss;
      this.renderer.appendChild(style, this.renderer.createText(css as string));
      this.renderer.appendChild(document.head, style);

      const safeHtml = this.sanitizer.bypassSecurityTrustHtml(loginPageHtml!);
      this.companyCoverRef.nativeElement.innerHTML = safeHtml as string

      // Favicon logic
      if (favicon) {
        let faviconLink = document.querySelector("link[rel~='icon']") as HTMLLinkElement;

        if (!faviconLink) {
          faviconLink = document.createElement('link');
          faviconLink.rel = 'icon';
          document.head.appendChild(faviconLink);
        }

        // To prevent caching issues (optional)
        faviconLink.href = `${favicon}?v=${new Date().getTime()}`;
      }

      this.companyLargeLogoRef.nativeElement.src = companyLogo as string
      this.companySmallLogoRef.nativeElement.src = smallLogoUrl as string
      this.loaderService.dismiss()

    }
  }

  private toRgbTriplet(color: string | undefined, fallback: string): string {
    if (!color) return fallback;

    const normalized = color.trim();
    const hexMatch = normalized.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (hexMatch) {
      let hex = hexMatch[1];
      if (hex.length === 3) {
        hex = hex.split('').map(ch => ch + ch).join('');
      }
      const r = Number.parseInt(hex.slice(0, 2), 16);
      const g = Number.parseInt(hex.slice(2, 4), 16);
      const b = Number.parseInt(hex.slice(4, 6), 16);
      return `${r}, ${g}, ${b}`;
    }

    const rgbMatch = normalized.match(/^rgb\s*\((\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i);
    if (rgbMatch) {
      return `${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}`;
    }

    return fallback;
  }





}
